import { ClockRegular, DismissRegular } from '@fluentui/react-icons';
import {
  AbsenceReasonCategory,
  AbsenceReasonResponse,
  AbsenceResponse,
  AbsenceState,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { groupBy } from 'lodash';
import React from 'react';
import { StudentAbsences } from './AbsenceEditDialog.interfaces';

export const getAbsenceIcon = (category: AbsenceReasonCategory) => {
  switch (category) {
    case AbsenceReasonCategory.none:
      return <DismissRegular className="absenceStatusIcon iconRed" />;
    case AbsenceReasonCategory.late:
    case AbsenceReasonCategory.leftEarly:
      return <ClockRegular className="absenceStatusIcon iconYellow" />;
  }
};

// If two absences exist for the same student in the same lesson,
// We either take the one with "LeftEarly" or the first one.
export const getMostImportantAbsencePerStudent = (
  absences: AbsenceResponse[],
): AbsenceResponse[] =>
  Object.values(groupBy(absences, absence => absence.affectedStudent.id)).map(
    groupedAbsences =>
      groupedAbsences.find(
        groupedAbsence =>
          groupedAbsence.absenceReason?.category ===
          AbsenceReasonCategory.leftEarly,
      ) ||
      groupedAbsences.find(a => a.state !== AbsenceState.unchecked) ||
      groupedAbsences[0],
  );

export const getGroupedAbsences = (
  absences: AbsenceResponse[],
): StudentAbsences[] => {
  const groupedAbsences: StudentAbsences[] = [];
  for (const absence of absences) {
    const indexOfGroupedAbsenceOfStudent = groupedAbsences.findIndex(
      groupedAbsence =>
        groupedAbsence.student.id === absence.affectedStudent.id,
    );
    if (indexOfGroupedAbsenceOfStudent > -1) {
      groupedAbsences[indexOfGroupedAbsenceOfStudent].absences.push(absence);
    } else {
      groupedAbsences.push({
        student: absence.affectedStudent,
        absences: [absence],
      });
    }
  }
  return groupedAbsences;
};

export const getUpdatedAbsenceList = (
  initialAbsences: StudentAbsences[],
  absenceToUpdate: AbsenceResponse,
  selectedAbsenceReason: AbsenceReasonResponse | null,
): StudentAbsences[] => {
  const temporaryAbsences = initialAbsences.find(
    studentAbsences =>
      studentAbsences.student.id === absenceToUpdate.affectedStudent.id,
  )?.absences;

  let item =
    temporaryAbsences?.find(a => a.state === AbsenceState.absent) ??
    temporaryAbsences?.[0];

  const updatedAbsencesList = [...initialAbsences];

  if (item) {
    const changedItem: AbsenceResponse = {
      ...item,
      absenceReason: selectedAbsenceReason,
      offset: undefined,
      state: selectedAbsenceReason ? AbsenceState.absent : AbsenceState.present,
    };
    updatedAbsencesList[
      initialAbsences.findIndex(
        studentAbsences =>
          studentAbsences.student.id === absenceToUpdate.affectedStudent.id,
      )
    ].absences = [changedItem];
  }

  return updatedAbsencesList;
};

export const updateAbsencesListWithLateOrLeftEarlyAbsence = (
  absenceToUpdate: AbsenceResponse,
  minutes: number,
  allAbsences: StudentAbsences[],
  replaceAbsence: boolean,
): StudentAbsences[] => {
  const absenceReason = {
    ...absenceToUpdate,
    offset: `00:${minutes.toString().padStart(2, '0')}:00`,
  };

  const studentAbsenceToUpdate = allAbsences.find(
    studentAbsence =>
      studentAbsence.student.id === absenceToUpdate.affectedStudent.id,
  );

  if (studentAbsenceToUpdate) {
    if (replaceAbsence) {
      const indexOfAbsenceToReplace =
        studentAbsenceToUpdate?.absences.findIndex(
          existingAbsence => existingAbsence.id === absenceReason.id,
        );
      if (indexOfAbsenceToReplace > -1)
        studentAbsenceToUpdate.absences[indexOfAbsenceToReplace] =
          absenceReason;
    } else {
      studentAbsenceToUpdate?.absences.push({ ...absenceReason, id: null });
    }
  }
  return allAbsences.map(studentAbsence =>
    studentAbsence.student.id === studentAbsenceToUpdate?.student.id
      ? studentAbsenceToUpdate
      : studentAbsence,
  );
};
